import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RFQ } from '../../../../types/rfq';
import { EmailAttachment } from '../../../../types/email';
import { listSavedRemarkFilePreviews } from '../../../../api/savedRemarks';
import { toast } from '../../../../utils/notifications';

export interface SavedRemarkFilePreview {
  id: number;
  name: string;
  size: number | null;
}

/** Base64 content of a file, without the data-URL prefix. */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        // Remove the data URL prefix (e.g., "data:image/png;base64,")
        const base64 = reader.result.split(',')[1] || reader.result;
        resolve(base64);
      } else {
        reject(new Error('Failed to convert file to base64'));
      }
    };
    reader.onerror = (error) => reject(error);
  });

/**
 * What the quote email carries besides the PDF: files the user attached
 * (dropped, picked, pasted, or an Excel the automation wrote), and the files
 * held by the saved remarks this RFQ uses.
 */
export function useQuoteAttachments(rfq: RFQ, open: boolean) {
  const [fileAttachments, setFileAttachments] = useState<EmailAttachment[]>([]);

  // Files held by the saved remarks this RFQ uses. These are listed here so the
  // user can see (and drop) what will go out, but they are NOT downloaded in the
  // browser and NOT carried in the send request - the backend resolves and
  // attaches them at send time. See services/savedRemarkAttachments.js for why.
  const [savedRemarkFiles, setSavedRemarkFiles] = useState<SavedRemarkFilePreview[]>([]);
  const [excludedSavedRemarkFileIds, setExcludedSavedRemarkFileIds] = useState<Set<number>>(new Set());

  // Track the RFQ ID to detect when switching to a different RFQ
  const previousRfqIdRef = useRef<number | null>(null);

  // Clear attachments when switching to a different RFQ
  useEffect(() => {
    if (previousRfqIdRef.current !== null && previousRfqIdRef.current !== rfq.rfq_id) {
      // RFQ changed - clear all attachments from previous RFQ
      setFileAttachments([]);
      setSavedRemarkFiles([]);
      setExcludedSavedRemarkFileIds(new Set());
    }
    previousRfqIdRef.current = rfq.rfq_id;
  }, [rfq.rfq_id]);

  const fetchSavedRemarkFiles = useCallback(async () => {
    if (!rfq.items || rfq.items.length === 0) {
      setSavedRemarkFiles([]);
      return;
    }

    try {
      const savedRemarkIds: number[] = Array.from(new Set(
        rfq.items
          .map(item => item.saved_remark_id)
          .filter((id): id is number => typeof id === 'number' && id > 0)
      ));

      if (savedRemarkIds.length === 0) {
        setSavedRemarkFiles([]);
        return;
      }

      const { data: files, error: filesError } = await listSavedRemarkFilePreviews(savedRemarkIds);

      if (filesError) {
        // Only the preview list is lost here; the backend still attaches the
        // files, so this must not block or warn the user off sending.
        console.error('Could not list saved remark files for preview:', filesError);
        setSavedRemarkFiles([]);
        return;
      }

      setSavedRemarkFiles(
        (files || []).map(file => ({
          id: file.id,
          name: file.original_filename,
          size: file.file_size ?? null
        }))
      );
    } catch (error) {
      console.error('Error listing saved remark files:', error);
      setSavedRemarkFiles([]);
    }
  }, [rfq.items]);

  const toggleSavedRemarkFile = useCallback((fileId: number) => {
    setExcludedSavedRemarkFileIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  // Keep the preview list in step with the items - this is a metadata-only
  // query, so there is nothing to gain from tracking which ids were already
  // fetched. It also covers applying a saved remark while the dialog is open.
  useEffect(() => {
    if (open) {
      fetchSavedRemarkFiles();
    }
  }, [open, fetchSavedRemarkFiles]);

  // Process file attachments
  const addFiles = useCallback(async (files: File[]) => {
    for (const file of files) {
      try {
        // Convert file to base64
        const base64Content = await fileToBase64(file);

        // Add to attachments
        setFileAttachments(prev => [
          ...prev,
          {
            name: file.name,
            contentType: file.type || 'application/octet-stream',
            content: base64Content
          }
        ]);

        toast({
          title: "File Attached",
          description: `${file.name} has been attached`,
          severity: "success"
        });
      } catch (error) {
        console.error(`Error processing file ${file.name}:`, error);
        toast({
          title: "Attachment Error",
          description: `Failed to attach ${file.name}`,
          severity: "error"
        });
      }
    }
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setFileAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  // A pasted image is attached as pasted_image_<timestamp>.png
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      // Check if the pasted content is an image
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          // Generate a filename with timestamp
          const timestamp = new Date().getTime();
          const filename = `pasted_image_${timestamp}.png`;

          // Create a new file with the generated name
          const renamedFile = new File([file], filename, { type: file.type });

          // Handle the file attachment
          addFiles([renamedFile]);
        }
      }
    }
  }, [addFiles]);

  return {
    fileAttachments,
    setFileAttachments,
    addFiles,
    removeAttachment,
    handlePaste,
    savedRemarkFiles,
    excludedSavedRemarkFileIds,
    toggleSavedRemarkFile,
  };
}
